/**
 * ui-search-results-pagination - A reusable component for paginated search functionality.
 * This component can be utilized for ANY search feature that requires pagination of results.
 */

import {
  Component,
  h,
  State,
  Prop,
  Event,
  EventEmitter,
  Watch,
  Listen,
  Element,
} from '@stencil/core';
import { findElementAsync } from '../../../common/find-element-async';

@Component({
  tag: 'ui-search-results-pagination',
  styleUrl: 'ui-search-results-pagination.scss',
})
export class UiSearchResultsPagination {
  @Element() el: HTMLElement;

  @Prop() results = 0;

  @Prop({ attribute: 'results-per-page' }) resultsPerPage = 10;

  @Prop() scrollIntoElementSelector?: string;

  @State() pages: number = this.results;

  @Prop({ mutable: true }) page? = 1;

  @Event() fetchResults: EventEmitter<{
    from: number;
  }>;

  @Watch('results')
  updatePages() {
    this.pages = Math.ceil(this.results / this.resultsPerPage);
  }

  componentWillLoad() {
    this.updatePages();
  }

  @Listen('uiSearchesPaginationChange', { target: 'window' })
  changePaginationAfterEventFromOutside() {
    this.fetchSearchResults({ page: 1, fromInside: false });
  }

  private fetchSearchResults({
    page = 1,
    fromInside = true,
  }: {
    page?: number;
    fromInside?: boolean;
  }) {
    const from = (page - 1) * this.resultsPerPage;
    this.page = page;
    if (fromInside) {
      this.fetchResults.emit({ from });
    }
  }

  private pagination() {
    const pages = [];
    // check is current page in first/last 4.
    const startIndicator = this.page - 4;
    const endIndicator = this.page + 4;

    // placeholders for first/last '...'
    let firstBreak = false;
    let secondBreak = false;

    // from/to which pages we will render paginator items - first and last one are always rendered
    let from = 2;
    let to = this.pages - 1;

    if (startIndicator > 0) {
      from = this.page - 2;
      firstBreak = true;
    }

    if (this.page > this.pages - 3 && this.pages - 5 > from) {
      from = this.pages - 5;
    }

    if (endIndicator < this.pages) {
      to = this.page + 2;
      secondBreak = true;
    }

    if (this.page < 3 && to > 5) {
      to = 5;
    }

    // first page
    pages.push(this.getPaginationItem(1, '1', ''));

    // first '...'
    if (firstBreak) {
      pages.push(this.getPaginationItem(null, '...', 'more'));
    }

    // pages
    for (let i = from; i <= to; i += 1) {
      pages.push(this.getPaginationItem(i, i.toString(), ''));
    }

    // second '...'
    if (secondBreak) {
      pages.push(this.getPaginationItem(null, '...', 'more'));
    }

    // last page
    pages.push(this.getPaginationItem(this.pages, this.pages.toString(), ''));

    return pages;
  }

  private getPaginationItem(index: number, label: string, cssClass: string) {
    const isCurrentPage = this.page === index;

    return (
      <li>
        <sd-button
          class={{ active: isCurrentPage, [cssClass]: true }}
          onClick={() => this.changePage(index)}
          onKeyDown={(event) => this.handleKeyDown(event, index)}
          variant="tertiary"
          aria-current={isCurrentPage ? 'page' : undefined}
          aria-label={
            isCurrentPage
              ? `Page ${index}, current page`
              : `Go to page ${index}`
          }
        >
          {label}
        </sd-button>
      </li>
    );
  }

  private getHeaderHeight(): number {
    const header = document.querySelector('ui-cms-header');
    return header?.shadowRoot?.querySelector('sd-header')?.clientHeight || 0;
  }

  private changePage(page) {
    this.fetchSearchResults({ page });
    this.focusFirstTeaserAndScrollToTop();
  }

  private async focusFirstTeaserAndScrollToTop() {
    const mainElement = await findElementAsync({
      selectorCallback: () =>
        this.el.closest(this.scrollIntoElementSelector || '[suggestions-api]'),
    });

    if (!mainElement) {
      return;
    }

    const headerHeight = this.getHeaderHeight();
    const { top } = mainElement.getBoundingClientRect();
    window.scrollTo({
      top: top - headerHeight + window.scrollY - 5,
      left: 0,
      behavior: 'smooth',
    });

    const firstTeaser = await findElementAsync<HTMLElement>({
      selectorCallback: () =>
        mainElement.querySelector('ui-cms-standard-teaser'),
    });

    if (firstTeaser) {
      firstTeaser.querySelector('a')?.focus();
    }
  }

  private handleKeyDown(event: KeyboardEvent, page: number) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.changePage(page);
    }
  }

  render() {
    return (
      <nav aria-label="Pagination navigation">
        <ul class="fetch-pagination">
          <li>
            <sd-button
              class="arrow previous"
              disabled={this.page === 1}
              variant="tertiary"
              onClick={() => this.changePage(this.page - 1)}
              onKeyDown={(event) => this.handleKeyDown(event, this.page - 1)}
              aria-label={`Go to previous page${this.page > 1 ? `, page ${this.page - 1}` : ''}`}
            >
              <sd-icon name="system/chevron-left" aria-hidden="true"></sd-icon>
            </sd-button>
          </li>
          {this.pagination()}
          <li>
            <sd-button
              class="arrow next"
              disabled={this.page === this.pages}
              variant="tertiary"
              onClick={() => this.changePage(this.page + 1)}
              onKeyDown={(event) => this.handleKeyDown(event, this.page + 1)}
              aria-label={`Go to next page${this.page < this.pages ? `, page ${this.page + 1}` : ''}`}
            >
              <sd-icon name="system/chevron-right" aria-hidden="true"></sd-icon>
            </sd-button>
          </li>
        </ul>
      </nav>
    );
  }
}
